/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiBadge,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiIconTip,
  EuiPanel,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { SaasAuthMfaDeviceResponse } from '@modules/cloud-api/v1/types'
import type { CuiTableColumn } from '@modules/cui/Table'
import { CuiTable } from '@modules/cui/Table'
import type { DeviceType } from '@modules/mfa-management/types'
import { deviceTypeIcon } from '@modules/mfa-lib/devices'

const SelectDevicesOption: React.FunctionComponent<{
  devices: SaasAuthMfaDeviceResponse[]
  type: DeviceType
  onSelect: (device: SaasAuthMfaDeviceResponse) => void
}> = ({ devices, type, onSelect }) => {
  if (devices.length === 0) {
    return null
  }

  const deviceNameColumn: CuiTableColumn<SaasAuthMfaDeviceResponse> = {
    label: null,
    render: (device) => {
      if (device.device_type === 'EMAIL') {
        return device.email_address
      }

      if (device.device_type === 'SMS') {
        return device.phone_number
      }

      return device.device_id //TODO: add name functionality
    },
    width: '70%',
  }

  const actionsColumn: CuiTableColumn<SaasAuthMfaDeviceResponse> = {
    label: null,
    render: (device) => (
      <EuiFlexGroup gutterSize='s' alignItems='flexEnd' justifyContent='flexEnd'>
        <EuiButton size='m' onClick={() => onSelect(device)}>
          <FormattedMessage id='selectDevice.setupButton' defaultMessage='Select' />
        </EuiButton>
      </EuiFlexGroup>
    ),
    align: 'right' as const,
    width: '30%',
    mobile: {
      align: 'right' as const,
    },
    actions: true,
  }

  const columns: Array<CuiTableColumn<SaasAuthMfaDeviceResponse>> = [
    deviceNameColumn,
    actionsColumn,
  ]

  const deviceIcon = deviceTypeIcon(type)

  return (
    <EuiPanel hasBorder={true}>
      <EuiFlexGroup alignItems='flexStart' direction='column' gutterSize='s'>
        <EuiFlexGroup alignItems='center' justifyContent='spaceBetween' gutterSize='m'>
          <EuiFlexItem grow={false}>
            <EuiIcon type={deviceIcon} size='l' />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <h2>
              <EuiText size='s'>
                <strong>
                  {type === 'GOOGLE' && (
                    <FormattedMessage
                      id='selectDevice.authenticator.title'
                      defaultMessage='Authenticate with your preferred app'
                    />
                  )}
                  {type === 'EMAIL' && (
                    <FormattedMessage
                      id='selectDevice.email.title'
                      defaultMessage='Email a verification code'
                    />
                  )}
                  {type === 'SMS' && (
                    <EuiFlexGroup gutterSize='s'>
                      <FormattedMessage
                        id='selectDevice.sms.title'
                        defaultMessage='Send a verification code to your phone'
                      />

                      <EuiBadge color='hollow'>
                        <FormattedMessage
                          id='authenticator.sms.deprecated'
                          defaultMessage='Deprecated'
                        />
                      </EuiBadge>

                      <EuiIconTip
                        title={
                          <FormattedMessage
                            id='mfa-management.sms.deprecated-tooltip-title'
                            defaultMessage='SMS authentication is going away'
                          />
                        }
                        content={
                          <FormattedMessage
                            id='mfa-management.sms.deprecated-tooltip-content'
                            defaultMessage="To enhance our security measures, we'll be removing this option in an upcoming update. Set up an alternative authentication method to avoid any future disruptions."
                          />
                        }
                        position='right'
                        type='question'
                        color='primary'
                        aria-label='Help'
                      />
                    </EuiFlexGroup>
                  )}
                  {type === 'WEBAUTHN' && (
                    <FormattedMessage
                      id='selectDevice.webauthn.title'
                      defaultMessage='Use your hardware security key or biometrics'
                    />
                  )}
                </strong>
              </EuiText>
            </h2>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiFlexItem grow={false}>
          <CuiTable
            data-test-id='select-device-list-table'
            rows={devices}
            columns={columns}
            getRowId={(device) => device.device_id}
            className='select-device-list-table'
          />
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPanel>
  )
}

export default SelectDevicesOption
